import { io } from "socket.io-client";

let socket;

// Several screens can share one room (e.g. the rider dashboard and wallet
// both use rider:<id>), so rooms are reference-counted — one screen
// unmounting must not pull the room out from under another. The server
// forgets every room on reconnect (network blip, laptop sleep), so they're
// re-joined on each "connect"; otherwise live updates silently stop.
const roomRefs = new Map();

function joinRoom(room) {
  const count = roomRefs.get(room) || 0;
  roomRefs.set(room, count + 1);
  if (count === 0) getSocket().emit("join", room);
}

function leaveRoom(room) {
  const count = roomRefs.get(room) || 0;
  if (count <= 1) {
    roomRefs.delete(room);
    getSocket().emit("leave", room);
  } else {
    roomRefs.set(room, count - 1);
  }
}

export function getSocket() {
  if (!socket) {
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:4000";
    let origin = apiUrl;
    let socketPath = "/socket.io";

    try {
      const parsed = new URL(apiUrl);
      // Socket.IO client needs the bare origin (scheme + host + port),
      // NOT the full URL with pathname. The sub-path goes into the `path`
      // option — passing a URL with a pathname causes the client to try
      // to connect to the wrong host/namespace and get a 404.
      origin = parsed.origin; // e.g. "https://fastsend.ng"
      if (parsed.pathname && parsed.pathname !== "/") {
        socketPath = `${parsed.pathname.replace(/\/$/, "")}/socket.io`;
        // e.g. "/prod-api/socket.io"
      }
    } catch (e) {}

    socket = io(origin, { path: socketPath, transports: ["polling", "websocket"] });
    socket.on("connect", () => {
      roomRefs.forEach((_, room) => socket.emit("join", room));
    });
  }
  return socket;
}

// User side — track a specific order's status + rider location + issue updates + chat messages.
export function trackOrder(orderId, { onStatus, onLocation, onIssue, onChatMessage, onChatRead } = {}) {
  const s = getSocket();
  joinRoom(`order:${orderId}`);
  if (onStatus) s.on("order:status", onStatus);
  if (onLocation) s.on("rider:location", onLocation);
  if (onIssue) s.on("order:issue", onIssue);
  if (onChatMessage) s.on("order:chat-message", onChatMessage);
  if (onChatRead) s.on("order:chat-read", onChatRead);
  return () => {
    leaveRoom(`order:${orderId}`);
    if (onStatus) s.off("order:status", onStatus);
    if (onLocation) s.off("rider:location", onLocation);
    if (onIssue) s.off("order:issue", onIssue);
    if (onChatMessage) s.off("order:chat-message", onChatMessage);
    if (onChatRead) s.off("order:chat-read", onChatRead);
  };
}

// Dedicated helper for in-delivery chat real-time messages
export function subscribeOrderChat(orderId, { onMessage, onRead } = {}) {
  const s = getSocket();
  joinRoom(`order:${orderId}`);
  if (onMessage) s.on("order:chat-message", onMessage);
  if (onRead) s.on("order:chat-read", onRead);
  return () => {
    leaveRoom(`order:${orderId}`);
    if (onMessage) s.off("order:chat-message", onMessage);
    if (onRead) s.off("order:chat-read", onRead);
  };
}

// Rider side — receive new-order-assigned pushes on this rider's own room.
export function listenForAssignments(riderId, onAssigned) {
  const s = getSocket();
  joinRoom(`rider:${riderId}`);
  s.on("order:assigned", onAssigned);
  return () => {
    leaveRoom(`rider:${riderId}`);
    s.off("order:assigned", onAssigned);
  };
}

// Rider side — a new job offer to accept/pass, updates to an issue this
// rider raised, and status changes (e.g. Support cancelling the order out
// from under them) on their currently-assigned order. Safe to call alongside
// listenForAssignments (same room).
export function listenForRiderEvents(riderId, { onOffered, onIssueUpdate, onStatusUpdate, onNewChatMessage, onWalletUpdate, onVirtualAccountReady } = {}) {
  const s = getSocket();
  joinRoom(`rider:${riderId}`);
  if (onOffered) s.on("order:offered", onOffered);
  if (onIssueUpdate) s.on("order:issue", onIssueUpdate);
  if (onStatusUpdate) s.on("order:status", onStatusUpdate);
  if (onNewChatMessage) s.on("chat:new-message", onNewChatMessage);
  if (onWalletUpdate) s.on("wallet:updated", onWalletUpdate);
  if (onVirtualAccountReady) s.on("virtual_account:ready", onVirtualAccountReady);
  return () => {
    leaveRoom(`rider:${riderId}`);
    if (onOffered) s.off("order:offered", onOffered);
    if (onIssueUpdate) s.off("order:issue", onIssueUpdate);
    if (onStatusUpdate) s.off("order:status", onStatusUpdate);
    if (onNewChatMessage) s.off("chat:new-message", onNewChatMessage);
    if (onWalletUpdate) s.off("wallet:updated", onWalletUpdate);
    if (onVirtualAccountReady) s.off("virtual_account:ready", onVirtualAccountReady);
  };
}

// Admin side — get notified the moment a dispatcher raises an issue.
export function listenForAdminIssues(onIssueRaised) {
  const s = getSocket();
  joinRoom("admins");
  if (onIssueRaised) s.on("order:issue-raised", onIssueRaised);
  return () => {
    leaveRoom("admins");
    if (onIssueRaised) s.off("order:issue-raised", onIssueRaised);
  };
}

// Admin side — get notified the moment a user submits a support ticket.
export function listenForAdminSupport(onTicketRaised) {
  const s = getSocket();
  joinRoom("admins");
  if (onTicketRaised) s.on("support:ticket-raised", onTicketRaised);
  return () => {
    leaveRoom("admins");
    if (onTicketRaised) s.off("support:ticket-raised", onTicketRaised);
  };
}

// Rider side — a live nudge that a new job opened on the job board (nobody
// nearby was found, or everyone passed/timed out) instead of needing a
// manual refresh to see it.
export function listenForJobBoard(onUpdate) {
  const s = getSocket();
  joinRoom("jobboard");
  if (onUpdate) s.on("jobboard:updated", onUpdate);
  return () => {
    leaveRoom("jobboard");
    if (onUpdate) s.off("jobboard:updated", onUpdate);
  };
}

// Customer side — live updates for any of this user's orders (status changes,
// dispatcher assigned, payment method switched) so dashboards and order lists
// refresh without the user having the Track page open.
export function listenForUserEvents(userId, { onOrderUpdate, onWalletUpdate, onNewChatMessage } = {}) {
  const s = getSocket();
  joinRoom(`user:${userId}`);
  if (onOrderUpdate) s.on("order:updated", onOrderUpdate);
  if (onWalletUpdate) s.on("wallet:updated", onWalletUpdate);
  if (onNewChatMessage) s.on("chat:new-message", onNewChatMessage);
  return () => {
    leaveRoom(`user:${userId}`);
    if (onOrderUpdate) s.off("order:updated", onOrderUpdate);
    if (onWalletUpdate) s.off("wallet:updated", onWalletUpdate);
    if (onNewChatMessage) s.off("chat:new-message", onNewChatMessage);
  };
}
